import React, { useEffect, useRef } from 'react';
import { downloadBlob, renderPagesPdf } from '../../pdfExport';
import { fmtDayShort, fmtRange, fixtureLine, toMin } from './plannerShared';
import { routineSummary } from './plannerCategories';

// The trainer's familiar three-page week sheet, generated from the week view (Club Planner,
// Phase 6): 1 · week schedule, 2 · Group 1 / Group 2 per day, 3 · meeting – video topics. It is
// the players' sheet: staff-only blocks, staff notes and staff topics never appear. Rendered
// off-screen in fixed print colours (not the app's theme variables), captured, and saved as PDF.

const INK = '#15171a';
const MUTED = '#5b616b';
const LINE = '#d9dce1';
const PAGE = { width: 1500, padding: 28, background: '#ffffff', color: INK, fontFamily: "'IBM Plex Sans', Arial, sans-serif", fontSize: 13, boxSizing: 'border-box' };
const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const HINT = { gk: 'GK', played: 'played', not_played: "didn't play", 'rot:1': 'G1', 'rot:2': 'G2' };
function audienceNote(tokens, nameOf) {
  if (!tokens || tokens.includes('all')) return '';
  const named = tokens.filter(t => t.startsWith('player:')).map(t => nameOf[Number(t.slice(7))] || `#${t.slice(7)}`);
  return [...tokens.filter(t => HINT[t]).map(t => HINT[t]), ...named].join(', ');
}

// One day's lines for the players' sheet, in time order.
function dayLines(blocks, nameOf) {
  const lines = [];
  blocks.filter(b => !b.audience.includes('staff')).forEach(b => {
    const start = b.resolved_start;
    const end = b.resolved_end;
    if (b.rotation && b.rotation_segments.length) {
      const swap = b.rotation.swap_at;
      const seg = (g, s) => b.rotation_segments.find(x => x.group === g && x.start === s);
      [[start, swap], [swap, end]].forEach(([s, e]) => {
        lines.push({ start: s, end: e, kind: 'rotation', title: `G1 ${seg(1, s)?.title || ''} · G2 ${seg(2, s)?.title || ''}`, location: null, points: s === start ? b.specific_points : null });
      });
      return;
    }
    const who = audienceNote(b.audience, nameOf);
    let title = b.title;
    if (b.category === 'routine' && b.routine) title = `${b.title} — ${routineSummary(b.routine)}`;
    if (b.category === 'arrival' && b.arrival_purpose === 'treatment') title = `${b.title} (optional)`;
    if (b.slot_minutes) title = `${title} (individual slots)`;
    lines.push({
      start, end, title: who ? `${title} (${who})` : title, location: b.location, points: b.specific_points,
      kind: b.category === 'game_routine' || b.category === 'match' ? 'game' : 'normal',
    });
  });
  return lines.sort((a, b) => (toMin(a.start) ?? 9999) - (toMin(b.start) ?? 9999));
}

function PageTitle({ week, title }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, borderBottom: `2px solid ${INK}`, paddingBottom: 8 }}>
      <span style={{ fontSize: 22, fontWeight: 800 }}>{title}</span>
      <span style={{ fontSize: 14, color: MUTED }}>Week {week.iso_week} · {fmtRange(week.monday, week.sunday)} {week.sunday.slice(0, 4)}</span>
    </div>
  );
}

function DayHead({ day }) {
  const game = day.type === 'game';
  const off = day.type === 'off';
  return (
    <div style={{ padding: '6px 8px', background: game ? INK : off ? '#e5e7eb' : '#f1f3f6', color: game ? '#fff' : INK, borderRadius: 4, marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
        <strong>{fmtDayShort(day.date)}</strong>
        <span style={{ fontFamily: MONO, fontSize: 11 }}>{day.label.text}</span>
      </div>
      {day.fixtures.map(f => <div key={f.id} style={{ fontSize: 12, fontWeight: 600 }}>{fixtureLine(f)} · {f.kickoff_time || 'KO TBC'}</div>)}
      {off && <div style={{ fontSize: 12, fontWeight: 700 }}>OFF</div>}
    </div>
  );
}

export function WeekSheet({ week, refs }) {
  const nameOf = Object.fromEntries(week.players.map(p => [p.id, p.name]));
  const byDay = Object.fromEntries(week.days.map(d => [d.date, dayLines(week.blocks.filter(b => b.date === d.date && b.resolved_start), nameOf)]));
  const groupDays = week.days.filter(d => d.groups[1].length || d.groups[2].length);
  const videoDays = week.days.map(d => ({
    day: d,
    meetings: week.blocks
      .filter(b => b.date === d.date && b.category === 'meeting_video' && !b.audience.includes('staff'))
      .sort((a, b) => (toMin(a.resolved_start) ?? 9999) - (toMin(b.resolved_start) ?? 9999))
      .map(b => ({ ...b, playerTopics: (b.topics || []).filter(t => t.audience === 'players') })),
  })).filter(x => x.meetings.length);

  return (
    <div aria-hidden="true" style={{ position: 'fixed', left: -100000, top: 0 }}>
      {/* 1 · Week schedule */}
      <div ref={el => { refs.current[0] = el; }} style={PAGE}>
        <PageTitle week={week} title="Schedule players" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
          {week.days.map(d => (
            <div key={d.date} style={{ background: d.type === 'off' ? '#f6f7f9' : '#fff' }}>
              <DayHead day={d} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 4 }}>
                {byDay[d.date].map((l, i) => (
                  <div key={i} style={{ borderBottom: `1px solid ${LINE}`, paddingBottom: 4, ...(l.kind === 'game' ? { background: INK, color: '#fff', padding: '3px 5px', borderRadius: 3 } : {}) }}>
                    <div style={{ fontFamily: MONO, fontSize: 11, color: l.kind === 'game' ? '#fff' : MUTED }}>{l.start}{l.end ? `–${l.end}` : ''}</div>
                    <div style={{ fontWeight: 600, lineHeight: 1.25 }}>{l.title}</div>
                    {l.location && <div style={{ fontSize: 11, color: l.kind === 'game' ? '#e5e7eb' : MUTED }}>{l.location}</div>}
                    {l.points && <div style={{ fontSize: 11, fontStyle: 'italic', whiteSpace: 'pre-wrap', marginTop: 2 }}>{l.points}</div>}
                  </div>
                ))}
                {!byDay[d.date].length && d.type !== 'off' && <div style={{ fontSize: 12, color: MUTED }}>—</div>}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: MUTED }}>Per block: time · schedule · specific points (in italics). Dark rows = game routine and match.</div>
      </div>

      {/* 2 · Group 1 / Group 2 per day */}
      <div ref={el => { refs.current[1] = el; }} style={PAGE}>
        <PageTitle week={week} title="Group 1 / Group 2" />
        {!groupDays.length && <div style={{ color: MUTED }}>No groups this week.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, groupDays.length)}, 1fr)`, gap: 12 }}>
          {groupDays.map(d => (
            <div key={d.date}>
              <DayHead day={d} />
              {[1, 2].map(g => (
                <div key={g} style={{ marginBottom: 10 }}>
                  <div style={{ fontWeight: 800, marginBottom: 2 }}>Group {g}</div>
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    {d.groups[g].map(id => <li key={id}>{nameOf[id] || `#${id}`}</li>)}
                  </ol>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* 3 · Meeting – video topics (player topics only) */}
      <div ref={el => { refs.current[2] = el; }} style={PAGE}>
        <PageTitle week={week} title="Meeting – video" />
        {!videoDays.length && <div style={{ color: MUTED }}>No video meetings this week.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {videoDays.map(({ day, meetings }) => (
            <div key={day.date} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, borderBottom: `1px solid ${LINE}`, paddingBottom: 10 }}>
              <DayHead day={day} />
              <div>
                {meetings.map(m => (
                  <div key={m.id} style={{ marginBottom: 6 }}>
                    <div><span style={{ fontFamily: MONO, color: MUTED }}>{m.resolved_start}–{m.resolved_end}</span> <strong>{m.title}</strong></div>
                    {m.playerTopics.length
                      ? <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>{m.playerTopics.map((t, i) => <li key={i}>{t.text}</li>)}</ul>
                      : <div style={{ color: MUTED, fontSize: 12 }}>No topics</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Renders the sheet off-screen, captures the three pages, downloads the PDF, then calls onDone.
export default function PlannerExport({ week, onDone }) {
  const refs = useRef([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
        const buf = await renderPagesPdf(refs.current.filter(Boolean), { orientation: 'landscape' });
        if (!cancelled) downloadBlob(new Blob([buf], { type: 'application/pdf' }), `Week ${week.iso_week} ${week.iso_year} schedule.pdf`);
        if (!cancelled) onDone(null);
      } catch (e) {
        if (!cancelled) onDone(e.message || 'PDF export failed');
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <WeekSheet week={week} refs={refs} />;
}

